// <copyright file="App.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import "src/App.css";
import "primeicons/primeicons.css";
import "primereact/resources/primereact.css";
import "primeflex/primeflex.css";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { decode as atob } from "base-64";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useState as _useState } from "react";
import { Route, Routes, useNavigate, Navigate } from "react-router-dom";
import AwsConsole from "src/components/AwsConsole/AwsConsole";
import Dashboards from "src/components/Dashboards/Dashboards";
import DataLakeExplorer from "src/components/DataLakeExplorer/DataLakeExplorer";
import HelpPage from "src/components/HelpPage/HelpPage";
import LoginPortal from "src/components/LoginPortal/LoginPortal";
import NotFoundPage from "src/components/NotFoundPage/NotFoundPage";
import ConfigContext from "src/context/ConfigContext";
import { Config } from "src/utils/ConfigLoader";
import {
    getCookie,
    getCookieJsonField,
    getUrlParamValue,
    Permission,
    checkFeatFlagandPermissions,
    checkConfigSectionFlag,
    checkDashboardPermissions,
    menuFocus,
} from "src/utils/SharedUtils";
import type { CognitoAccessTokenPayload } from "aws-jwt-verify/jwt-model";
import type { MenuItem } from "primereact/menuitem";
import type { ChangeEvent, FC } from "react";
import type { NavigateFunction } from "react-router-dom";
import type { IConfig } from "src/utils/ConfigLoader";

interface AppProps {
    useState?: typeof _useState; // optional hook override (for tests)
    tokenPayload?: CognitoAccessTokenPayload | null; // optional verified token payload override (for tests)
}

export enum TokenStatus {
    /* eslint-disable autofix/no-unused-vars */
    valid = "valid",
    invalid = "invalid",
    expired = "expired",
    permissiondenied = "permissiondenied",
    /* eslint-enable */
}

const sessionDuration = 3600; // Default session duration in seconds
const tokenRefreshInterval = 60000; //Default interval to check users token state and expiration
const toast = React.createRef<Toast>();
const mainMenu = React.createRef<Menu>();
const userMenu = React.createRef<Menu>();

/**
 * Sign out of the user interface via the login identify provider
 * @param cognitoClientId Cognito client that issues tokens for this website
 */
function signOut(cognitoClientId: string, configData: IConfig) {
    const idpCookie = getCookie("idp");
    document.cookie = "DepSessionAccess=; SameSite=Strict"; // Clear OID access token cookie
    document.cookie = "DepSessionIdentity=; SameSite=Strict"; // Clear OID identity token cookie
    document.cookie = "idp=; SameSite=Strict"; // Clear identity provider cookie
    document.cookie = `awsSessionInfo=; SameSite=Strict`; // Clear AWS session info cookie
    if (idpCookie == "cognito" && cognitoClientId) {
        // Use the logout endpoint for Cognito & redirect to uri (uri must be configured in Cogito app client)
        const uri = `${configData.cognitoAuth.cognitoAuthUrl}/logout?client_id=${cognitoClientId}&logout_uri=${configData.logoutRedirectUrl}`;
        window.location.assign(encodeURI(uri));
    } else if (idpCookie == "azure") {
        // Use the logout endpoint for Azure AD & redirect to uri
        const uri = `${configData.cognitoAuth.cognitoAuthUrl}/logout?client_id=${cognitoClientId}&logout_uri=${configData.logoutRedirectUrl}`;
        window.location.assign(encodeURI(uri));
    } else {
        // Redirect to uri
        if (!configData.logoutRedirectUrl) {
            throw new Error(
                "Application missing required configuration: logoutRedirectUrl"
            );
        } else {
            window.location.assign(configData.logoutRedirectUrl);
        }
    }
}

/**
 * Create user interface main menu item
 * @param navigate Function that navigates to an app route
 * @param setSearchText Function that updates search text in the component state
 * @param label The navigation label
 * @param path The navigation path
 * @param icon The icon displayed (optional)
 * @returns A main menu item that navigates to a view route and clears the menu search text input
 */
function createMainMenuItem(
    navigate: NavigateFunction,
    setSearchText: React.Dispatch<React.SetStateAction<string>>,
    label: string,
    path: string,
    icon = null
): MenuItem {
    return {
        label: label,
        icon: icon,
        command: () => navigateMainRoute(navigate, setSearchText, path),
    } as MenuItem;
}

/**
 * Navigate to a user interface main menu item
 * @param navigate Function that navigates to an app route
 * @param setSearchText Function that updates search text in the component state
 * @param path The navigation path
 */
function navigateMainRoute(
    navigate: NavigateFunction,
    setSearchText: React.Dispatch<React.SetStateAction<string>>,
    path: string
): void {
    setSearchText("");
    navigate(path);
}

/**
 * Update the state of search text and show/hide the main menu
 * @param event Event that triggered the change handler
 * @param setSearchText Function that updates search text in the component state
 */
function searchChanged(
    event: ChangeEvent<HTMLInputElement>,
    setSearchText: React.Dispatch<React.SetStateAction<string>>
): void {
    const inputVal: string = event.target.value;
    setSearchText(inputVal);
    if (inputVal.length > 1) {
        mainMenu.current?.show(event);
    } else {
        mainMenu.current?.hide(event);
    }
}

/**
 * Load the identity and access OpenId (OID) tokens from the url params or website cookies,
 * and validate the access token. NOTE: front-end validation is for convenience only and
 * can be spoofed maliciously, so back end validation is also required.
 *
 * @param setIsLoading Function that updates is-loading status in the component state
 * @param setTokenStatus Function that updates token status in the component state
 * @param cognitoClientId Cognito client that issues tokens for this website
 * @param setUserName Function that updates user name in the component state
 * @param tokenPayload For testing only, allows mock payloads that are not verified as signed by Cognito
 */
async function loadOidTokens(
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setTokenStatus: React.Dispatch<React.SetStateAction<TokenStatus>>,
    cognitoClientId: string,
    setUserName: React.Dispatch<React.SetStateAction<string>>,
    configData: IConfig,
    tokenPayload?: CognitoAccessTokenPayload | null
) {
    setIsLoading(true);
    const accessToken =
        getUrlParamValue({
            param: "access_token",
            slicer: "#",
            delimiter: "&",
        }) ?? getCookieJsonField("DepSessionAccess", "token");
    const idToken =
        getUrlParamValue({
            param: "id_token",
            slicer: "#",
            delimiter: "&",
        }) ?? getCookieJsonField("DepSessionIdentity", "token");
    console.debug(`loadOidTokens accessToken: ${accessToken?.slice(0, 50)}...`);
    console.debug(`loadOidTokens idToken: ${idToken?.slice(0, 50)}...`);
    if (!accessToken || !idToken || [accessToken, idToken].includes("")) {
        setTokenStatus(TokenStatus.invalid);
        setIsLoading(false);
    } else {
        setTokenStatus(
            await validateToken(
                accessToken,
                idToken,
                cognitoClientId,
                configData,
                null,
                setUserName,
                tokenPayload
            )
        );
        setIsLoading(false);
    }
}

/**
 * Validate OpenID access token; if valid set cookies for access and identity token
 * @param accessToken The OID access token string to validate
 * @param idToken The OID identity token to associate with the session
 * @param cognitoClientId Cognito client that issues tokens for this website
 * @param configData Data from app config
 * @param currentToken Current App Token State
 * @param setUserName Function that updates user name in the component state
 * @param tokenPayload For testing only, allows mock payloads that are not verified as signed by Cognito
 * @returns Token validation status (valid|invalid|expired)
 */
async function validateToken(
    accessToken: string,
    idToken: string,
    cognitoClientId: string,
    configData: IConfig,
    currentToken?: TokenStatus | null,
    setUserName?: React.Dispatch<React.SetStateAction<string>>,
    tokenPayload?: CognitoAccessTokenPayload | null
): Promise<TokenStatus> {
    console.debug(`validateToken accessToken: ${accessToken.slice(0, 50)}...`);
    try {
        // Verify token is signed by the expected token issuer
        const payload =
            tokenPayload ??
            (await getVerifiedTokenPayload(
                cognitoClientId,
                accessToken,
                configData
            ));
        if (!payload) {
            if (currentToken && currentToken == TokenStatus.valid) {
                return TokenStatus.expired;
            }
            console.debug("Invalid open ID token signature");
            return TokenStatus.invalid;
        }

        // Verify audience matches a known Cognito app integration for this website
        const clientId = payload.client_id?.toString() ?? "invalidClientId";
        if (
            !configData.identityProviders
                .map((a) => a.clientId)
                .includes(clientId)
        ) {
            console.debug("Invalid open ID token audience");
            return TokenStatus.invalid;
        }

        // Verify issuer matches Cognito user pool for this website
        if (!payload.iss.endsWith(configData.cognitoAuth.cognitoUserPoolId)) {
            console.debug("Invalid open ID token issuer");
            return TokenStatus.invalid;
        }

        // Verify token use claim
        if (payload.token_use !== "access") {
            console.debug("Invalid open ID token use claim");
            return TokenStatus.invalid;
        }

        // Verify the token is not expired
        if (payload.exp * 1000 <= Date.now()) {
            console.debug("Invalid open ID token expiration date");
            return TokenStatus.expired;
        }

        // Set user info and website cookie from token info
        const accessTokStr = accessToken.split(".")[1];
        const accessTokStrPadded = accessTokStr.padEnd(
            accessTokStr.length +
                (accessTokStr.length % 4 === 0
                    ? 0
                    : 4 - (accessTokStr.length % 4)),
            "="
        );
        const accessTokPayload = JSON.parse(atob(accessTokStrPadded));
        if (setUserName) setUserName(accessTokPayload["username"]);
        const accessTokvalue = `{ "token": "${accessToken}", "expiryTs": ${accessTokPayload["exp"]} }`;
        document.cookie = `DepSessionAccess=${accessTokvalue}; Max-Age=${sessionDuration}; SameSite=Strict`;

        // Also set identity token website cookie
        const idTokStr = idToken.split(".")[1];
        const idTokStrPadded = idTokStr.padEnd(
            idTokStr.length +
                (idTokStr.length % 4 === 0 ? 0 : 4 - (idTokStr.length % 4)),
            "="
        );
        const idTokPayload = JSON.parse(atob(idTokStrPadded));
        const idTokVal = `{ "token": "${idToken}", "expiryTs": ${idTokPayload["exp"]} }`;
        document.cookie = `DepSessionIdentity=${idTokVal}; Max-Age=${sessionDuration}; SameSite=Strict`;
        // Check the user permissions
        if (!idTokPayload["permission"].includes(Permission.UILogin)) {
            console.debug("Invalid user permissions");
            return TokenStatus.permissiondenied;
        }

        // Token validation succeeded
        return TokenStatus.valid;
    } catch (error) {
        console.debug("Invalid open ID token: " + error);
        return TokenStatus.invalid;
    }
}

/**
 * Verify a token was signed by the Cognito issuer for this website
 * @param cognitoClientId Cognito client that issues tokens for this website
 * @param token Token to verify
 * @returns Verified token payload, rejected if not verified
 */
async function getVerifiedTokenPayload(
    cognitoClientId: string,
    token: string,
    configData: IConfig
): Promise<CognitoAccessTokenPayload | null> {
    try {
        const verifier = CognitoJwtVerifier.create({
            userPoolId: configData.cognitoAuth.cognitoUserPoolId,
            tokenUse: "access",
            clientId: cognitoClientId,
        });
        return await verifier.verify(token);
    } catch {
        return null;
    }
}

/**
 * Create a welcome page component
 * @returns A component that can be rendered in a browser
 */
function WelcomePage() {
    return (
        <p>
            Welcome, please select an item from the menu bar above to get
            started.
        </p>
    );
}

/**
 * React application functional component
 * @returns A component that can be rendered in a browser
 */
const App: FC<AppProps> = ({ useState = _useState, tokenPayload = null }) => {
    /**
     * Instantiate the Configuration instance
     */
    /* eslint-disable autofix/no-unused-vars */
    const [configData, setConfigData] = useState<IConfig | null>(null);
    const [cognitoClientId, setCognitoClientId] = useState<string>("unknown");
    const idpCookie = getCookie("idp");
    const [isLoading, setIsLoading] = useState(true);
    const [tokenStatus, setTokenStatus] = useState(TokenStatus.invalid);
    const [userName, setUserName] = useState("");
    const routeWelcome = <Route key="0" path="/" element={<WelcomePage />} />;
    const routeAwsConsole = (
        <Route key="1" path="/aws-console" element={<AwsConsole />} />
    );
    const routeDashboards = (
        <Route key="2" path="/dashboards" element={<Dashboards />} />
    );
    const routeDataLakeExplorer = (
        <Route
            key="3"
            path="/data-lake-explorer"
            element={<DataLakeExplorer />}
        />
    );
    const routeHelpPages = (
        <Route key="4" path="/help" element={<HelpPage />} />
    );
    const routeLogin = <Route key="5" path="/" element={<LoginPortal />} />;
    const routeRedirect = (
        <Route key="6" path="*" element={<Navigate to="/" replace />} />
    );
    const routeNotFound = <Route key="7" path="*" element={<NotFoundPage />} />;

    const routes = [
        routeAwsConsole,
        routeDashboards,
        routeDataLakeExplorer,
        routeHelpPages,
        routeWelcome,
        routeNotFound,
    ];
    const noTokenRoutes = [routeRedirect, routeLogin, routeHelpPages];
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const mainMenuItems: MenuItem[] = [
        // AWS Console
        configData &&
        checkFeatFlagandPermissions(
            "aws-console",
            configData.featureFlags,
            Permission.UIAwsConsoleRead
        )
            ? createMainMenuItem(
                  navigate,
                  setSearchText,
                  "AWS Console",
                  routeAwsConsole.props.path
              )
            : { visible: false },
        // Dashboards
        configData &&
        checkConfigSectionFlag("dashboards", configData.featureFlags) &&
        checkDashboardPermissions()
            ? createMainMenuItem(
                  navigate,
                  setSearchText,
                  "Dashboards",
                  routeDashboards.props.path
              )
            : { visible: false },
        // Data Lake Explorer (DLE)
        configData &&
        checkFeatFlagandPermissions("data-lake-explorer", configData.featureFlags, Permission.UIDataLakeExplorerRead)
            ? createMainMenuItem(
                  navigate,
                  setSearchText,
                  "Data Lake Explorer",
                  routeDataLakeExplorer.props.path
              )
            : { visible: false },
    ];
    const userMenuItems: MenuItem[] = [
        { label: `Username: ${userName}` },
        {
            label: "Logout",
            icon: "pi pi-user-minus",
            command: () =>
                configData ? signOut(cognitoClientId, configData) : null,
        },
    ];

    /**
     * When component mounts/unmounts, reload and validate the open ID token
     */
    useEffect(() => {
        if (!configData) {
            Config.fetch()
                .then((config) => {
                    if (config != configData) {
                        setConfigData(config);
                        setCognitoClientId(
                            config.identityProviders.find(
                                (c) => c.name === idpCookie
                            )?.clientId ?? "unknown"
                        );
                        if (config?.theme) {
                            console.debug(`theme: ${config.theme}`);
                            /*eslint-disable prefer-const*/
                            let themeLink = document.getElementById(
                                "app-theme"
                            ) as HTMLLinkElement;
                            console.debug(`themeLink: ${themeLink.href}`);
                            if (themeLink) {
                                themeLink.href = `${config.theme}`;
                                console.debug(`themeLink: ${themeLink.href}`);
                            }
                        }
                        document.title = config.title;
                    }
                })
                .catch(console.error);
        }
    }, []);

    /**
     * When configData state changes, reload and validate the open ID token
     */
    useEffect(() => {
        if (configData) {
            loadOidTokens(
                setIsLoading,
                setTokenStatus,
                cognitoClientId,
                setUserName,
                configData,
                tokenPayload
            );
        }
    }, [configData]);

    /**
     * When tokenStatus state changes, reset token status refresh timer
     */
    useEffect(() => {
        if (configData) {
            const interval = setInterval(
                () => refreshTokenStatus(),
                tokenRefreshInterval
            );
            return function cleanup() {
                // Function called when component unmounted or cleaned between renders
                clearInterval(interval);
            };
        }
    }, [tokenStatus]);

    /**
     * Refresh the Token Status
     * It is done this way so we do not unecessarily update the component state
     * resetting the users view, the state will only be updated if the token status has changed.
     */
    const refreshTokenStatus = async () => {
        if (configData) {
            const accessToken =
                getUrlParamValue({
                    param: "access_token",
                    slicer: "#",
                    delimiter: "&",
                }) ?? getCookieJsonField("DepSessionAccess", "token");
            const idToken =
                getUrlParamValue({
                    param: "id_token",
                    slicer: "#",
                    delimiter: "&",
                }) ?? getCookieJsonField("DepSessionIdentity", "token");
            let currentTokenStatus = TokenStatus.invalid;
            if (
                !accessToken ||
                !idToken ||
                [accessToken, idToken].includes("")
            ) {
                currentTokenStatus = TokenStatus.invalid;
            } else if (accessToken && idToken) {
                currentTokenStatus = await validateToken(
                    accessToken,
                    idToken,
                    cognitoClientId,
                    configData,
                    tokenStatus
                );
            }
            if (currentTokenStatus != tokenStatus) {
                setTokenStatus(currentTokenStatus);
                if (currentTokenStatus !== TokenStatus.valid) {
                    if (currentTokenStatus == TokenStatus.expired) {
                        toast.current?.show({
                            severity: "error",
                            summary: "Session Expired",
                            detail: "Session Expired, Please Re-Login",
                            sticky: true, // Show until dismissed by user
                        });
                    } else if (
                        currentTokenStatus == TokenStatus.permissiondenied
                    ) {
                        toast.current?.show({
                            severity: "error",
                            summary: "Permission Denied",
                            detail: "This user does not have permissions to view this resource. Please contact your administrator",
                            sticky: true, // Show until dismissed by user
                        });
                    }
                    navigate(routeLogin.props.path);
                }
            }
        }
    };

    /**
     * Webpage header component
     */
    const header = () => {
        if (!configData) {
            return null;
        }
        return (
            <header className="App-header">
                {configData.logos?.owner ? (
                    <img
                        src={configData.logos.owner}
                        className="App-logo"
                        alt="logo-owner"
                    />
                ) : null}
                <img
                    src={configData.logos?.application}
                    className="App-logo"
                    alt="logo-app"
                />
                {tokenStatus == TokenStatus.valid ? (
                    <>
                        <label htmlFor="search-main-menu" className="mr-2">
                            Search menu
                        </label>
                        <Menu
                            title="Main menu"
                            id="main-menu"
                            data-testid="main-menu"
                            className="App-menu"
                            popup
                            aria-haspopup={true}
                            ref={mainMenu}
                            model={mainMenuItems.filter((i) =>
                                i.label
                                    ?.toLocaleLowerCase()
                                    .match(searchText.toLocaleLowerCase())
                            )}
                            onShow={() => menuFocus(mainMenu)}
                            onHide={() => document.getElementById('main-menu-button')?.focus()}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    mainMenu.current?.hide(e);
                                    setTimeout(() => document.getElementById('main-menu-button')?.focus(), 0);
                                }
                            }}
                        />
                        <InputText
                            id="search-main-menu"
                            className="App-search"
                            value={searchText}
                            onChange={(e) => searchChanged(e, setSearchText)}
                            aria-controls="main-menu"
                            aria-haspopup={true}
                            data-testid="main-menu-search"
                        />
                        <Button
                            title="Main menu"
                            aria-controls="main-menu"
                            id="main-menu-button"
                            data-testid="main-menu-button"
                            icon="pi pi-list"
                            className="p-button p-button-text p-button-icon-only custom-focus-highlight"
                            onClick={(e) => mainMenu.current?.toggle(e)}
                            onKeyDown={(e) => {
                                if (e.key === ' ') {
                                    e.preventDefault();
                                    mainMenu.current?.toggle(e);
                                }
                            }}
                        />
                    </>
                ) : null}
                <div className="App-button-panel">
                    <Button
                        title="Help info"
                        aria-controls="help-info"
                        data-testid="help-button"
                        icon="pi pi-question-circle"
                        className="p-button p-button-text p-button-icon-only custom-focus-highlight"
                        onClick={() => navigate(routeHelpPages.props.path)}
                        onKeyDown={(e) => {
                            if (e.key === ' ') {
                                e.preventDefault();
                                navigate(routeHelpPages.props.path);
                            }
                        }}
                    />
                    {tokenStatus == TokenStatus.valid ? (
                        <>
                            <Menu
                                title="User menu"
                                id="user-menu"
                                data-testid="user-menu"
                                className="App-menu"
                                popup
                                aria-haspopup={true}
                                ref={userMenu}
                                model={userMenuItems}
                                onShow={() => menuFocus(userMenu)}
                                onHide={() => document.getElementById('user-menu-button')?.focus()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        userMenu.current?.hide(e);
                                        setTimeout(() => document.getElementById('user-menu-button')?.focus(), 0);
                                    }
                                }}
                            />
                            <Button
                                title="User menu"
                                aria-controls="user-menu"
                                id="user-menu-button"
                                data-testid="user-menu-button"
                                icon="pi pi-user"
                                className="App-nav-icon p-button p-button-text p-button-icon-only custom-focus-highlight"
                                onClick={(e) => userMenu.current?.toggle(e)}
                                onKeyDown={(e) => {
                                    if (e.key === ' ') {
                                        e.preventDefault();
                                        userMenu.current?.toggle(e);
                                    }
                                }}
                            />
                        </>
                    ) : null}
                </div>
            </header>
        );
    };

    /**
     * Webpage footer component
     */
    const footer = () => {
        if (!configData) {
            return null;
        }
        /*eslint-disable react/jsx-key*/
        return (
            <footer className="App-footer">
                <div className="grid">
                    <div className="col-4">
                        <div className="App-footer_logos">
                            {configData.logos?.owner ? (
                                <img
                                    src={configData.logos.owner}
                                    className="App-logo-footer"
                                    alt="logo-owner"
                                />
                            ) : (
                                <img
                                    src={configData.logos?.application}
                                    className="App-logo-footer"
                                    alt="logo-app"
                                />
                            )}
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="card">
                            <h4>USEFUL LINKS</h4>
                            <hr />
                            <ul>
                                {configData.footer?.usefulLinks.map((item) => (
                                    <li key={item.url}>
                                        <a href={item.url}> {item.text} </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="card">
                            <h4>CONTACT US</h4>
                            <hr />
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `${configData.footer?.contactUs.join(
                                        "<br>"
                                    )}`,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </footer>
        );
    };

    /**
     * JSX for the main view
     */
    if (isLoading || !configData) {
        return <ProgressSpinner className="App-spinner" aria-label="Loading" />;
    } else if (tokenStatus !== TokenStatus.valid) {
        return (
            <div className="App">
                <ConfigContext.Provider value={configData}>
                    {header()}
                    <Toast ref={toast} />
                    <Routes>{noTokenRoutes}</Routes>
                    {footer()}
                </ConfigContext.Provider>
            </div>
        );
    } else {
        return (
            <div className="App">
                <ConfigContext.Provider value={configData}>
                    {header()}
                    <Toast ref={toast} />
                    <div className="App-content">
                        <Routes>{routes}</Routes>
                    </div>
                    {footer()}
                </ConfigContext.Provider>
            </div>
        );
    }
};

export default App;
